// Third party
import { SortingRule } from 'react-table';

// Redux
import { AsyncResult, Action, ApiAction, Result } from '../index';
import api from '../redux-api';

// Utils
import { ADVANCED_SEARCH_OPTIONS } from '../../utils/constants';

// Types
import { AdvancedSearchState } from './types/advanced-search';
import { LOCALE } from '../../utils/i18n';
import SearchOptions from '@/types/search-options';
import Fund from '@/types/fund';
import ComparisonBenchmark from '@/types/comparison-benchmark';
import Locale from '@/types/locale';

// Initial State
const INITIAL_STATE: AdvancedSearchState = {
  loadingResults: false,
  loadingFilters: false,
  loadingQuickAddItems: false,
  searchOptions: {},
  preLoadQuery: '',
  hasMore: true,
  fund: {
    filtersData: {
      classifications: [],
      manufacturers: [],
      shelves: [],
      universes: [],
    },
    selectedFunds: [],
  },
  benchmark: {
    filtersData: {
      classifications: [],
      universes: [],
      currencies: [],
      assetTypes: [],
    },
    selectedBenchmarks: [],
  },
  quickAddItems: [],
};

// Actions
const RESET_SEARCH: Action = {
  type: 'connect/advanced-search/reset',
};

const SEARCH_ALL_FUNDS: ApiAction = {
  type: 'connect/advanced-search/search-all-funds',
  endpoint: 'funds/search/',
};

const UPDATE_FUNDS_RESULT: Action = {
  type: 'connect/advanced-search/update-funds-result',
};

const SEARCH_ALL_BENCHMARKS: ApiAction = {
  type: 'connect/advanced-search/search-all-benchmarks',
  endpoint: 'comparison-benchmarks/search/',
};

const UPDATE_SEARCH_OPTIONS: Action = {
  type: 'connect/advanced-search/update-options',
};

const LOAD_SEARCH_FILTERS: ApiAction = {
  type: 'connect/advanced-search/load-filters',
  endpoint: 'search/filters',
};

const LOAD_QUICK_ADD_ITEMS: ApiAction = {
  type: 'connect/advanced-search/load-quick-add-items',
  endpoint: 'search/quickAddItems',
};

const SET_SELECTED_FUNDS: Action = {
  type: 'connect/advanced-search/set-selected-funds',
};

const REMOVE_SELECTED_FUNDS: Action = {
  type: 'connect/advanced-search/remove-selected-fund',
};

const ADD_SELECTED_FUNDS: Action = {
  type: 'connect/advanced-search/add-selected-fund',
};

const SET_SELECTED_BENCHMARKS: Action = {
  type: 'connect/advanced-search/set-selected-benchmarks',
};

const SET_FOCUSED_FUND: Action = {
  type: 'connect/advanced-search/set-focused-fund',
};

const SET_FOCUSED_BENCHMARK: Action = {
  type: 'connect/advanced-search/set-focused-benchmark',
};

const LOAD_MORE_FUND_RESULTS: ApiAction = {
  type: 'connect/advanced-search/load-more-fund-results',
  endpoint: 'funds/search/',
};

const LOAD_MORE_BENCHMARK_RESULTS: ApiAction = {
  type: 'connect/advanced-search/load-more-benchmark-results',
  endpoint: 'comparison-benchmarks/search/',
};

const SET_PRELOAD_QUERY: Action = {
  type: 'connect/advanced-search/set-preload-query',
};

const CLEAR_RESULTS: Action = {
  type: 'connect/advanced-search/clear-results',
};

const GET_QUICK_ADD_PROXIES: ApiAction = {
  type: 'connect/advanced-search/get-quick-add-proxies',
  endpoint: '',
};

// Reducer
export default function reducer(state: AdvancedSearchState = INITIAL_STATE, action: Action): AdvancedSearchState {
  switch (action.type) {
    case RESET_SEARCH.type: {
      return {
        ...state,
        searchOptions: {},
        hasMore: true,
        fund: {
          filtersData: {
            classifications: [],
            manufacturers: [],
            shelves: [],
            universes: [],
          },
          selectedFunds: [],
          focusedFund: undefined,
        },
        benchmark: {
          filtersData: {
            classifications: [],
            currencies: [],
            universes: [],
            assetTypes: [],
          },
          selectedBenchmarks: [],
        },
      };
    }
    case UPDATE_FUNDS_RESULT.type: {
      return {
        ...state,
        fund: {
          ...state.fund,
          funds: action.funds,
        },
      };
    }
    case SEARCH_ALL_FUNDS.type: {
      return {
        ...state,
        loadingResults: true,
        totalCount: undefined,
        hasMore: true,
        fund: {
          ...state.fund,
          funds: undefined,
        },
      };
    }
    case `${SEARCH_ALL_FUNDS.type}_SUCCESS`: {
      return {
        ...state,
        totalCount: action.data.count,
        loadingResults: false,
        fund: {
          ...state.fund,
          funds: action.data.funds,
        },
      };
    }
    case `${SEARCH_ALL_FUNDS.type}_ERROR`: {
      return {
        ...state,
        loadingResults: false,
      };
    }
    case SEARCH_ALL_BENCHMARKS.type: {
      return {
        ...state,
        loadingResults: true,
        totalCount: undefined,
        hasMore: true,
        benchmark: {
          ...state.benchmark,
          benchmarks: undefined,
        },
      };
    }
    case `${SEARCH_ALL_BENCHMARKS.type}_SUCCESS`: {
      return {
        ...state,
        totalCount: action.data.count,
        loadingResults: false,
        benchmark: {
          ...state.benchmark,
          benchmarks: action.data.benchmarks,
        },
      };
    }
    case `${SEARCH_ALL_BENCHMARKS.type}_ERROR`: {
      return {
        ...state,
        loadingResults: false,
      };
    }
    case UPDATE_SEARCH_OPTIONS.type: {
      return {
        ...state,
        searchOptions: action.options,
      };
    }
    case LOAD_SEARCH_FILTERS.type: {
      return {
        ...state,
        loadingFilters: true,
        fund: {
          ...state.fund,
          filtersData: {
            classifications: [],
            manufacturers: [],
            shelves: [],
            universes: [],
          },
        },
        benchmark: {
          ...state.benchmark,
          filtersData: {
            classifications: [],
            currencies: [],
            universes: [],
            assetTypes: [],
          },
        },
      };
    }
    case `${LOAD_SEARCH_FILTERS.type}_SUCCESS`: {
      return {
        ...state,
        loadingFilters: false,
        fund: {
          ...state.fund,
          filtersData: action.data.data.fundFilters,
        },
        benchmark: {
          ...state.benchmark,
          filtersData: action.data.data.benchmarkFilters,
        },
      };
    }
    case `${LOAD_SEARCH_FILTERS.type}_ERROR`: {
      return {
        ...state,
        loadingFilters: false,
      };
    }
    case LOAD_QUICK_ADD_ITEMS.type: {
      return {
        ...state,
        loadingQuickAddItems: true,
        quickAddItems: [],
      };
    }
    case `${LOAD_QUICK_ADD_ITEMS.type}_SUCCESS`: {
      return {
        ...state,
        loadingQuickAddItems: false,
        quickAddItems: action.data.data,
      };
    }
    case `${LOAD_QUICK_ADD_ITEMS.type}_ERROR`: {
      return {
        ...state,
        loadingQuickAddItems: false,
      };
    }
    case SET_SELECTED_FUNDS.type: {
      return {
        ...state,
        fund: {
          ...state.fund,
          selectedFunds: action.funds,
        },
      };
    }
    case ADD_SELECTED_FUNDS.type: {
      const selectedFundIds = state.fund.selectedFunds.map(fund => fund._id);
      const fundsToAdd = action.funds.filter((fund: Fund) => !selectedFundIds.includes(fund._id));
      return {
        ...state,
        fund: {
          ...state.fund,
          selectedFunds: [...state.fund.selectedFunds, ...fundsToAdd],
        },
      };
    }
    case REMOVE_SELECTED_FUNDS.type: {
      const idsToRemove = action.funds.map((fund: Fund) => fund._id);
      return {
        ...state,
        fund: {
          ...state.fund,
          selectedFunds: state.fund.selectedFunds.filter(fund => !idsToRemove.includes(fund._id)),
        },
      };
    }
    case SET_FOCUSED_FUND.type: {
      return {
        ...state,
        fund: {
          ...state.fund,
          focusedFund: action.fund,
        },
      };
    }
    case SET_FOCUSED_BENCHMARK.type: {
      return {
        ...state,
        benchmark: {
          ...state.benchmark,
          focusedBenchmark: action.benchmark,
        },
      };
    }
    case `${LOAD_MORE_FUND_RESULTS.type}_SUCCESS`: {
      if (!action.data.funds.length) {
        return {
          ...state,
          hasMore: false,
        };
      }
      return {
        ...state,
        fund: {
          ...state.fund,
          funds: (state.fund.funds || []).concat(action.data.funds),
        },
      };
    }
    case `${LOAD_MORE_BENCHMARK_RESULTS.type}_SUCCESS`: {
      if (!action.data.benchmarks.length) {
        return {
          ...state,
          hasMore: false,
        };
      }
      return {
        ...state,
        benchmark: {
          ...state.benchmark,
          benchmarks: (state.benchmark.benchmarks || []).concat(action.data.benchmarks),
        },
      };
    }
    case SET_PRELOAD_QUERY.type: {
      return {
        ...state,
        preLoadQuery: action.preLoadQuery,
      };
    }
    case CLEAR_RESULTS.type: {
      return {
        ...state,
        searchOptions: {},
        fund: {
          ...state.fund,
          funds: undefined,
          selectedFunds: [],
          focusedFund: undefined,
        },
        benchmark: {
          ...state.benchmark,
          benchmarks: undefined,
          selectedBenchmarks: [],
        },
        totalCount: undefined,
        hasMore: true,
      };
    }
    case SET_SELECTED_BENCHMARKS.type: {
      return {
        ...state,
        benchmark: {
          ...state.benchmark,
          selectedBenchmarks: action.benchmarks,
        },
      };
    }
    default:
      return state;
  }
}

// Action Creators
export const resetSearch = (): Result<void> => dispatch => {
  dispatch({ type: RESET_SEARCH.type });
};

export const searchAllFunds =
  (opts: SearchOptions, sorting?: SortingRule[]): AsyncResult<Fund[]> =>
  (dispatch, getState) => {
    const {
      intl: { locale },
    } = getState();

    const options = { locale, ...opts, sort: sorting ? sorting.map((sort: SortingRule) => `${sort.id} ${sort.desc ? 'DESC' : 'ASC'}`) : [] };

    dispatch({ type: UPDATE_SEARCH_OPTIONS.type, options });

    return api.get(SEARCH_ALL_FUNDS, options);
  };

export const updateFundsResult =
  (funds: Fund[]): Result<void> =>
  dispatch =>
    dispatch({ type: UPDATE_FUNDS_RESULT.type, funds });

export const searchAllBenchmarks =
  (opts: SearchOptions, sorting?: SortingRule[]): AsyncResult<ComparisonBenchmark[]> =>
  (dispatch, getState) => {
    const {
      intl: { locale },
    } = getState();

    const options = { locale, ...opts, sort: sorting ? sorting.map((sort: SortingRule) => `${sort.id} ${sort.desc ? 'DESC' : 'ASC'}`) : [] };

    dispatch({ type: UPDATE_SEARCH_OPTIONS.type, options });
    return api.get(SEARCH_ALL_BENCHMARKS, options);
  };

export const updateSearchSorting =
  (sorting: SortingRule[], selectedOption: ADVANCED_SEARCH_OPTIONS): Result<void> =>
  (dispatch, getState) => {
    const {
      advancedSearch: { searchOptions },
      intl: { locale },
    } = getState();

    const options = { ...searchOptions, sort: sorting.map((sort: SortingRule) => `${sort.id} ${sort.desc ? 'DESC' : 'ASC'}`) };

    dispatch({ type: UPDATE_SEARCH_OPTIONS.type, options });
    switch (selectedOption) {
      case ADVANCED_SEARCH_OPTIONS.FUNDS:
      case ADVANCED_SEARCH_OPTIONS.PROXIES: {
        return api.get(SEARCH_ALL_FUNDS, { ...options, locale });
      }
      case ADVANCED_SEARCH_OPTIONS.BENCHMARKS: {
        return api.get(SEARCH_ALL_BENCHMARKS, { ...options, locale });
      }
      default: {
        return null;
      }
    }
  };

export const loadSearchFilters =
  (overrideLocale?: Locale): AsyncResult<void> =>
  (dispatch, getState) => {
    let {
      intl: { locale },
    } = getState();

    if (overrideLocale) {
      locale = overrideLocale;
    }

    // TODO - Support en-US filters [CON-8332]
    if (locale === LOCALE.EN_US) {
      locale = LOCALE.EN_CA;
    }

    return api.get(LOAD_SEARCH_FILTERS, { locale });
  };

export const loadQuickAddItems = (): AsyncResult<void> => (dispatch, getState) => {
  const {
    intl: { locale },
  } = getState();
  return api.get(LOAD_QUICK_ADD_ITEMS, { locale });
};

export const setSelectedFunds =
  (funds: Fund[]): Result<void> =>
  dispatch => {
    dispatch({ type: SET_SELECTED_FUNDS.type, funds });
  };

export const removeSelectedFunds =
  (funds: Fund[]): Result<void> =>
  dispatch => {
    dispatch({ type: REMOVE_SELECTED_FUNDS.type, funds });
  };

export const addSelectedFunds =
  (funds: Fund[]): Result<void> =>
  dispatch => {
    dispatch({ type: ADD_SELECTED_FUNDS.type, funds });
  };

export const setSelectedBenchmarks =
  (benchmarks: ComparisonBenchmark[]): Result<void> =>
  dispatch => {
    dispatch({ type: SET_SELECTED_BENCHMARKS.type, benchmarks });
  };

export const setFocusedFund =
  (fund?: Fund): Result<void> =>
  dispatch => {
    dispatch({ type: SET_FOCUSED_FUND.type, fund });
  };

export const setFocusedBenchmark =
  (benchmark?: ComparisonBenchmark): Result<void> =>
  dispatch => {
    dispatch({ type: SET_FOCUSED_BENCHMARK.type, benchmark });
  };

export const loadMoreFundResults = (): AsyncResult<void> => (dispatch, getState) => {
  const {
    advancedSearch: { fund, searchOptions },
    intl: { locale },
  } = getState();

  const currentFunds = fund.funds || [];

  const query = { ...searchOptions, start: currentFunds.length + 1, locale };

  return api.get(LOAD_MORE_FUND_RESULTS, query);
};

export const loadMoreBenchmarkResults = (): AsyncResult<void> => (dispatch, getState) => {
  const state = getState().advancedSearch;
  const currentBenchmarks = state.benchmark.benchmarks || [];

  const query = { ...state.searchOptions, start: currentBenchmarks.length + 1 };

  return api.get(LOAD_MORE_BENCHMARK_RESULTS, query);
};

export const setPreloadQuery =
  (preLoadQuery: string): Result<void> =>
  dispatch => {
    dispatch({ ...SET_PRELOAD_QUERY, preLoadQuery });
  };

export const clearResults = (): Result<void> => dispatch => {
  dispatch({ type: CLEAR_RESULTS.type });
};

export const getQuickAddProxies = (): AsyncResult<void> => () => api.get(GET_QUICK_ADD_PROXIES);
